import React, { useMemo } from 'react'
import TranslationContext from './TranslationContext'
import esDict from './locales/es.json'
import frDict from './locales/fr.json'
import { SUPPORTED_LANGS } from './supported-langs'

const dictionaries: Record<string, Record<string, string>> = {
  es: esDict,
  fr: frDict
}

export interface TranslationProviderProps {
  lang: string
  dictionary?: Record<string, string>
  children: React.ReactNode
}

export default function TranslationProvider({ lang, dictionary, children }: TranslationProviderProps) {
  const value = useMemo(() => {
    const dict = dictionary ?? dictionaries[lang] ?? {}
    const __ = (key: string, ...args: (string | number)[]) => {
      // Defensive: callers translate live data fields (item names, area labels) whose
      // runtime value can be null/undefined despite the string type. Return it as-is
      // rather than warning, looking up `undefined`, or throwing on the .replace below.
      if (typeof key !== 'string') return key == null ? '' : String(key)
      const translated = dict[key]
      if (translated === undefined) {
        if (
          process.env.NODE_ENV !== 'production' &&
          lang !== 'en' &&
          (SUPPORTED_LANGS as readonly string[]).includes(lang)
        ) {
          console.warn(`[i18n] Missing translation for lang="${lang}": "${key}"`)
        }
        const fallback = key
        if (!args.length) return fallback
        return fallback.replace(/%s|%d/g, () => String(args.shift() ?? ''))
      }
      if (!args.length) return translated
      return translated.replace(/%s|%d/g, () => String(args.shift() ?? ''))
    }
    return { lang, __ }
  }, [lang, dictionary])

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>
}
